import { compactDecrypt } from 'jose';
import forge from "node-forge";

const jweKey = import.meta.env.VITE_JWE_KEY;
const base64Key = import.meta.env.VITE_BASE64_KEY;
const NONCE = import.meta.env.VITE_NONCE;
const UMEncChabi = import.meta.env.VITE_UME_NC_CHABI;
const SEPARATOR = '|';
const RESET_PIN_KEY  = import.meta.env.VITE_RESET_PIN;

// ==========================================
// 1. FAST SINGLE-PASS UTILITIES
// ==========================================
const bufferToBase64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));

const base64ToBuffer = (str) => {
  try {
    const padded = str.padEnd(str.length + (4 - (str.length % 4)) % 4, "=");
    return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
  } catch (err) {
    throw new Error(`Base64 decode failed: ${err.message}`);
  }
};

// ==========================================
// 2. CACHED STATIC KEYS (Compiled once on app mount)
// ==========================================
const keyUint8 = new TextEncoder().encode(jweKey);
const fixedNonce = new TextEncoder().encode(NONCE);
let cachedAesKey = null;
let cachedForgeKey = null;
let cachedResetPinKey = null;

const getAesKey = async () => {
  if (!cachedAesKey) {
    cachedAesKey = await window.crypto.subtle.importKey(
      'raw',
      base64ToBuffer(base64Key),
      'AES-GCM',
      true,
      ['encrypt', 'decrypt']
    );
  }
  return cachedAesKey;
};

const getResetPinKey = async () => {
  if (!cachedResetPinKey) {
    cachedResetPinKey = await window.crypto.subtle.importKey(
      'raw',
      base64ToBuffer(RESET_PIN_KEY),
      'AES-GCM',
      true,
      ['encrypt', 'decrypt']
    );
  }
  return cachedResetPinKey;
};

const getForgePublicKey = () => {
  if (!cachedForgeKey) {
    cachedForgeKey = forge.pki.publicKeyFromPem(UMEncChabi);
  }
  return cachedForgeKey;
};

// ==========================================
// 3. OPTIMIZED CRYPTO FUNCTIONS
// ==========================================

// JWE Token Decrypt
export const DecryptJweToken = async (jweToken) => {
  try {
    const { plaintext } = await compactDecrypt(jweToken, keyUint8);
    const decodedText = new TextDecoder().decode(plaintext);
  
    try {
      return JSON.stringify(JSON.parse(decodedText), null, 2);
    } catch {
      return decodedText;
    }
  } catch (err) {
    throw new Error(`Decryption failed: ${err.message}`);
  }
};

// MW Encrypt
export const EncryptText = async (data) => {
  const key = await getAesKey();
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encodedData = new TextEncoder().encode(JSON.stringify(data));

  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    encodedData
  );

  const encryptedArray = new Uint8Array(encryptedBuffer);
  const ciphertext = encryptedArray.subarray(0, -16); 
  const authTag = encryptedArray.subarray(-16);

  return {
    iv: bufferToBase64(iv),
    encryptedMessage: bufferToBase64(ciphertext),
    authTag: bufferToBase64(authTag)
  };
};

// MW Decrypt
export const DecryptText = async (encryptedData) => {
  const key = await getAesKey();
  const iv = base64ToBuffer(encryptedData.iv);
  const cipherBytes = base64ToBuffer(encryptedData.encryptedMessage);
  const tagBytes = base64ToBuffer(encryptedData.authTag);

  const combined = new Uint8Array(cipherBytes.length + tagBytes.length);
  combined.set(cipherBytes, 0);
  combined.set(tagBytes, cipherBytes.length);

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    combined.buffer
  );

  const decryptedText = new TextDecoder().decode(decryptedBuffer);
  try {
    return JSON.parse(decryptedText);
  } catch {
    return decryptedText;
  }
};

// CMS Encrypt
export async function CMSEncrypt(data, alternativeKey = base64Key) {
  try {
    const key = alternativeKey === base64Key 
      ? await getAesKey() 
      : await window.crypto.subtle.importKey('raw', base64ToBuffer(alternativeKey), 'AES-GCM', false, ['encrypt']);
      
    const encodedData = new TextEncoder().encode(JSON.stringify(data));
    const encrypted = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv: fixedNonce },
      key,
      encodedData
    );

    const encryptedArray = new Uint8Array(encrypted);
    const ciphertext = encryptedArray.subarray(0, -16);
    const authTag = encryptedArray.subarray(-16);

    return `${bufferToBase64(fixedNonce)}${SEPARATOR}${bufferToBase64(ciphertext)}${SEPARATOR}${bufferToBase64(authTag)}`;
  } catch (error) {
    return error.message;
  }
}
// CMS Encrypt Pin Reset
export async function CMSEncryptPin(data, alternativeKey = RESET_PIN_KEY) {
  try {
    const key = alternativeKey === RESET_PIN_KEY 
      ? await getResetPinKey() 
      : await window.crypto.subtle.importKey('raw', base64ToBuffer(alternativeKey), 'AES-GCM', false, ['encrypt']);
      
    const encodedData = new TextEncoder().encode(JSON.stringify(data));
    const encrypted = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv: fixedNonce },
      key,
      encodedData
    );

    const encryptedArray = new Uint8Array(encrypted);
    const ciphertext = encryptedArray.subarray(0, -16);
    const authTag = encryptedArray.subarray(-16);

    return `${bufferToBase64(fixedNonce)}${SEPARATOR}${bufferToBase64(ciphertext)}${SEPARATOR}${bufferToBase64(authTag)}`;
  } catch (error) {
    return error.message;
  }
}

// CMS Decrypt
export async function CMSDecrypt(encryptedData) {
  try {
    const parts = encryptedData.split(SEPARATOR);
    if (parts.length !== 3) throw new Error('Invalid format');

    // FIXED: Correctly targeting split indexes, [1], and [2]
    const iv = base64ToBuffer(parts[0]);
    const cipherBytes = base64ToBuffer(parts[1]);
    const tagBytes = base64ToBuffer(parts[2]);
    const key = await getAesKey();

    const combined = new Uint8Array(cipherBytes.length + tagBytes.length);
    combined.set(cipherBytes, 0);
    combined.set(tagBytes, cipherBytes.length);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      key,
      combined.buffer
    );

    const decryptedText = new TextDecoder().decode(decryptedBuffer);
   
    try {
      return JSON.parse(decryptedText); 
    } catch {
      return decryptedText.replace(/^"|"$/g, '');
    }
  } catch (error) {
    return encryptedData;
  }
}

// User Management RSA Encryption
export const UMEncrypt = (data) => {
  try {
    const publicKey = getForgePublicKey();
    const encryptedBytes = publicKey.encrypt(data, "RSAES-PKCS1-V1_5");
    return forge.util.encode64(encryptedBytes);
  } catch (err) {
    throw err;
  }
};
